/** Contract: docs/backend/HORIZON_REMMI_FRONTEND(2).md §3, §4 and §10. */

export type HorizonInterestLevel = 'high' | 'medium' | 'low';
export type HorizonEngagementLevel = 'high' | 'medium' | 'low';
export type HorizonAccessStatus = 'not_invited' | 'invited' | 'active' | 'suspended';
export type HorizonAccessAction = 'suspend' | 'restore' | 'cancel-invite';

export interface HorizonAccess {
  eligible: boolean;
  ineligible_reason: 'project_not_enabled' | 'no_email' | 'lead_inactive' | null;
  email: string | null;
  status: HorizonAccessStatus;
  invited_at: string | null;
  invited_by: string | null;
  last_invite_at: string | null;
  invite_expires_at: string | null;
  activated_at: string | null;
  last_login_at: string | null;
  member_id: string | null;
  /** This lead's project is the customer's main project — the one Horizon opens on (§3.1). */
  is_primary_project?: boolean;
}

export interface HorizonSummaryMetric {
  value: number;
  /** Against the preceding period of the same length; null when no comparison is possible. */
  change_pct: number | null;
  /** The preceding period's figure; 0 or null means there was nothing to compare with. */
  previous_value?: number | null;
  /** Absent means available; false means the metric is not tracked yet and must be hidden. */
  available?: boolean;
}

/**
 * A percentage is shown only against a real previous-period figure: growth from zero came back as "100%",
 * which reads as a comparison that never happened. Until `previous_value` is sent, `change_pct` alone decides.
 */
export function hasPeriodComparison(metric: HorizonSummaryMetric | null): boolean {
  if (metric?.change_pct === null || metric?.change_pct === undefined) {
    return false;
  }
  return metric.previous_value === undefined || (metric.previous_value ?? 0) > 0;
}

export interface HorizonLeadSummary {
  available: boolean;
  visits: HorizonSummaryMetric;
  pages_viewed: HorizonSummaryMetric;
  updates_viewed: HorizonSummaryMetric;
  documents_viewed: HorizonSummaryMetric;
  documents_downloaded: HorizonSummaryMetric;
  /** Every request; `information_requests` and `display_suite_bookings` are the split of it. */
  requests: HorizonSummaryMetric;
  information_requests: HorizonSummaryMetric;
  display_suite_bookings: HorizonSummaryMetric;
  time_spent_seconds: HorizonSummaryMetric;
  properties_viewed: HorizonSummaryMetric;
  shortlisted: HorizonSummaryMetric;
}

export type HorizonStage = 'enquiry' | 'in_progress' | 'construction' | 'purchased';

export const HORIZON_STAGES: { label: string; value: HorizonStage }[] = [
  { label: 'Enquiry', value: 'enquiry' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Construction', value: 'construction' },
  { label: 'Purchased', value: 'purchased' },
];

export interface HorizonLeadOverview {
  has_account: boolean;
  status: 'invited' | 'active' | 'suspended' | 'pending';
  last_active_at: string | null;
  /** Where the buyer is on this project; Horizon shows its content for that stage. Absent means `enquiry`. */
  horizon_stage?: HorizonStage;
  generated_at: string;
  range: { from: string; to: string };
  summary: HorizonLeadSummary;
  engagement: { level: HorizonEngagementLevel; score: number; note: string | null };
  insights: {
    available: boolean;
    projects_viewed: { project_id: string; name: string; views: number }[];
    most_viewed_sections: { key: string; label: string; views: number }[];
    ai_insight: string | null;
  };
}

export interface HorizonActivityItem {
  id: string;
  at: string;
  type: string;
  /** Icon name from the backend, e.g. `download`, `eye`. */
  icon: string;
  /** Normalised for highlighting: `login`, `page_view`, `document_download`, `property_view`, `shortlist`, `information_request`, `message`, `booking`… */
  kind?: string;
  /** Page views: the Horizon page in words ("the project home", "Project Info · Floorplans"). */
  label?: string | null;
  /** Always null: activity rows never link to a Remmi route. */
  link?: null;
  title: string;
  subtitle?: string;
  duration_ms?: number;
  /** Requests (information requests, messages, bookings, callbacks): what the buyer sent, when the backend has it. */
  /** Request rows (contract §4.2): what the buyer sent; each field is null when the portal sent none. */
  detail?: {
    request_type?: string;
    message?: string | null;
    topic?: string | null;
    preferred?: string | null;
    lot_name?: string | null;
    phone?: string | null;
  };
}

export interface HorizonActivityPage {
  items: HorizonActivityItem[];
  page: number;
  limit: number;
  total: number;
  has_more: boolean;
}

export interface HorizonLeadDocument {
  document_id: string;
  name: string;
  project_name: string;
  file_type: string;
  views: number;
  downloads: number;
  last_accessed_at: string;
  thumbnail?: string;
  /** Ready-made staff URL for the file. Null for a document shared before the field existed — the row is then read-only. */
  url: string | null;
  library_file_id: string | null;
  library_path: string | null;
  /** Stored S3 key. */
  file_path: string | null;
}

export const HORIZON_DATE_RANGES = [
  { value: 7, label: 'Last 7 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 90, label: 'Last 90 Days' },
  { value: 0, label: 'All Time' },
];

export const HORIZON_ACTIVITY_FILTERS = [
  { value: 'all', label: 'All Activity' },
  { value: 'pages', label: 'Pages' },
  { value: 'documents', label: 'Documents' },
  { value: 'updates', label: 'Updates' },
  { value: 'properties', label: 'Properties' },
  { value: 'shortlists', label: 'Shortlists' },
  { value: 'shares', label: 'Shared Items' },
  { value: 'requests', label: 'Requests' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'account', label: 'Account' },
];

/** Phrases for the backend's `ineligible_reason`, shown as the invite button's tooltip. */
export const HORIZON_INELIGIBLE_REASONS: Record<string, string> = {
  project_not_enabled: 'Horizon is not enabled for this project.',
  no_email: 'This lead has no email address.',
  lead_inactive: 'This lead is not active.',
};

export type HorizonLotStatus = string;

export interface HorizonLeadProperty {
  unit_id: string;
  lot_name?: string;
  name: string;
  project_id: string;
  project_name: string;
  image?: string;
  views: number;
  time_spent_seconds: number;
  last_viewed_at: string;
  shortlisted: boolean;
  shortlisted_at: string | null;
  floorplan_views: number;
  /** The lot's status in Remmi. */
  status: HorizonLotStatus;
  bed: number;
  bath: number;
  car: number;
  price: number;
}

export interface HorizonHeatMapNode {
  /** Unit id at property level, else the building / floor key. */
  id: string;
  label: string;
  kind: 'precinct' | 'building' | 'floor' | 'unit';
  /** Percent of the image width / height (0-100). */
  x: number;
  y: number;
  /** Optional outline, in the same percent coordinates. */
  polygon?: [number, number][];
  score: number;
  interest: HorizonInterestLevel;
  views: number;
  unique_leads?: number;
  /** The level this node opens; null for a leaf. */
  drill_to: 'masterplan' | 'building' | 'floor' | null;
  /** Sent as-is as the query of the next level's request; the backend decides which keys that level uses. */
  drill_params?: Record<string, string>;
  unit_id?: string;
}

export interface HorizonHeatMap {
  available: boolean;
  level: 'masterplan' | 'building' | 'floor';
  image: string | null;
  image_width: number;
  image_height: number;
  legend: { key: HorizonInterestLevel; label: string }[];
  nodes: HorizonHeatMapNode[];
}

// ---- Buyer interest insights + AI (docs/backend/horizon/horizon-lead-tab-redesign.md §4, §10) ----

export type HorizonAiStatus = 'ok' | 'insufficient_data' | 'unavailable';

export interface HorizonInsights {
  available: boolean;
  range: { from: string; to: string };
  preferred_configuration: { bedrooms: number; label: string; share_pct: number } | null;
  preferred_floors: { from: number; to: number; label: string } | null;
  price_range_viewed: { min: number; max: number; label: string } | null;
  property_types_viewed: { type: string; views: number }[];
  engagement: { level: HorizonEngagementLevel; score: number; note: string | null };
  ai_insight: {
    status: HorizonAiStatus;
    text: string | null;
    recommended_action: string | null;
    generated_at: string | null;
  } | null;
}

// ---- Project heat map on the price list matrix (§3) ----

export type HorizonInterestLevelOrNone = 'high' | 'medium' | 'low' | 'none';

export type HorizonLotEngagementState = 'viewed' | 'repeated' | 'shortlisted' | 'shared' | 'not_viewed';

export interface HorizonMatrixLot {
  unit_id: string;
  lot_name: string;
  /** Position on the level row, from the matrix. */
  column: string;
  price: number;
  /** The lot's own sales status. Never mixed with the engagement colour. */
  availability: string;
  floorplan?: { id: string; name: string } | null;
  bed?: number;
  /** The project report colours by this; the lead tab colours by `engagement.state`. */
  interest?: { level: HorizonInterestLevelOrNone; score: number };
  engagement: {
    state: HorizonLotEngagementState;
    views: number;
    unique_viewers: number;
    repeat_viewers: number;
    shortlists: number;
    shares: number;
    enquiries?: number;
  };
}

export type HorizonMatrixViewBy = 'buildings' | 'levels' | 'floorplans';

export interface HorizonMatrixHeatmap {
  available: boolean;
  view_by: HorizonMatrixViewBy;
  buildings: { building_id: string; name: string; levels: string[] }[];
  selected: { building_id: string; level: string };
  columns: string[];
  lots: HorizonMatrixLot[];
  legend: { key: HorizonLotEngagementState; label: string }[];
  /** Floor plans on this level or building, for the floor plan filter. */
  floorplans?: { id: string; name: string }[];
  /** The legend for the report's interest colours; `thresholds` are the cut-offs behind them. */
  interest_legend?: { key: string; label: string }[];
  thresholds?: { high: number; medium: number };
}

// ---- Shared with buyer (§5) ----

export interface HorizonShareLot {
  unit_id: string;
  name: string;
  project_name: string;
  /** The floor plan, else the project photo: the picture the buyer sees in Shared With Me. */
  image: string | null;
  /** Which of the two `image` is; null when the project has neither. */
  image_source?: 'floorplan' | 'project' | null;
  /** The floor plan only; null when none is linked. */
  floorplan_image: string | null;
  bed: number;
  bath: number;
  car: number;
  internal_area: number | null;
  balcony_area: number | null;
  total_area: number | null;
  availability: string;
}

export interface HorizonShare {
  share_id: string;
  type: 'lot' | 'document';
  status: 'new' | 'viewed';
  shared_at: string;
  shared_by: { id: string; name: string; avatar: string | null };
  message: string | null;
  viewed_at: string | null;
  view_count: number;
  lot: HorizonShareLot | null;
  /**
   * `thumbnail` (~400px) for the card and `image` (~1600px) to open; for a PDF both are a picture of its first page
   * made by the backend, and `null` until that picture exists (contract §12.4a).
   */
  document: { item_id?: string; name: string; file_type: string; size_label: string | null; thumbnail: string | null; image?: string | null } | null;
  download_count?: number;
}

export interface HorizonSharesPage {
  summary: { properties: number; documents: number };
  items: HorizonShare[];
  page: number;
  limit: number;
  total: number;
  has_more: boolean;
}

/** What the agent may share for the project: its lots, and only the documents an admin approved. */
export interface HorizonShareable {
  lots: {
    unit_id: string;
    name: string;
    level: string | null;
    bed: number;
    bath: number;
    car: number;
    price: number;
    availability: string;
    availability_label?: string;
    image?: string | null;
    /** The lot's floor plan, shared along with it; null when no plan is linked. */
    floorplan?: { name: string; document_id: string | null; library_file_id: string | null; shareable_item_id: string | null } | null;
  }[];
  /** The library's folders in its own order; a document's `category_order` is its folder's position here. */
  categories?: { id: string; name: string; order: number }[];
  /** Every file in the project's library; any of them can be shared as `library_file`, no approval needed. */
  library_files?: {
    library_file_id: string;
    name: string;
    folder: string | null;
    file_type: string;
    size_label: string | null;
    size_bytes?: number | null;
    thumbnail: string | null;
    /** The Sharing Library item for this file, when there is one. */
    shareable_item_id: string | null;
  }[];
  documents: {
    id: string;
    name: string;
    file_type: string;
    size_label: string | null;
    size_bytes?: number | null;
    thumbnail: string | null;
    category_id?: string;
    category_name: string;
    category_order?: number;
    /** "Recently Added" sorts on this. */
    added_at?: string | null;
    updated_at?: string | null;
  }[];
}

/** `document` is a Sharing Library item; `library_file` is any file of the project's library. */
export interface HorizonShareItemRef {
  type: 'lot' | 'document' | 'library_file';
  id: string;
}

// ---- Buyer preferences + shortlist (§6) ----

export interface HorizonPreferences {
  budget_min: number | null;
  budget_max: number | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  car_spaces: number | null;
  features: string[];
  additional_requirements: string | null;
}

export interface HorizonPreferencesResponse {
  available: boolean;
  project_id: string;
  feature_labels?: { key: string; label: string }[];
  preferences: HorizonPreferences | null;
  updated_at: string | null;
  updated_by: { type: 'buyer' | 'agent'; id: string; name: string } | null;
}

/** One choice for bedrooms / bathrooms / car spaces, e.g. `{ value: 4, label: "4+", or_more: true }`. */
export interface HorizonCountOption {
  value: number;
  label: string;
  or_more?: boolean;
  lots?: number;
}

export interface HorizonPreferenceOptions {
  available?: boolean;
  /** The project's price range; the budget must sit inside it. Null when no lot has a price. */
  budget?: { min: number; max: number; step?: number; currency?: string } | null;
  property_types: { key: string; label: string }[];
  features: { key: string; label: string }[];
  /** Objects from the backend; plain numbers are accepted too. */
  bedrooms: (HorizonCountOption | number)[];
  bathrooms: (HorizonCountOption | number)[];
  car_spaces: (HorizonCountOption | number)[];
}

export interface HorizonShortlistItem {
  unit_id: string;
  name: string;
  price: number;
  bed: number;
  bath: number;
  car: number;
  image: string | null;
  shortlisted_at: string;
  availability: string;
}

// ---- Project-wide panels (§7, §8) ----

export interface HorizonProjectProperty {
  unit_id: string;
  lot_name?: string;
  name: string;
  project_id: string;
  views: number;
  unique_leads?: number;
}

export interface HorizonSharedDocument {
  document_id: string;
  name: string;
  category: string;
  file_type: string;
  size_label: string | null;
  thumbnail: string | null;
  /** Opens for staff. */
  url: string | null;
}
